/**
 * Binom API Hooks — list/get campaigns + their flows.
 * Routes to /binom/* endpoints; rows tagged `provider:'binom'`.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery } from '@tanstack/react-query';

import api from '@/services/api';
import type {
  Campaign,
  CampaignDetail,
  CampaignProvider,
  Flow,
} from '@/hooks/voluum/useVoluum';

export const binomKeys = {
  all: ['binom'] as const,
  campaigns: () => [...binomKeys.all, 'campaigns'] as const,
  campaign: (id: string) => [...binomKeys.campaigns(), id] as const,
  campaignFlows: (id: string) =>
    [...binomKeys.campaign(id), 'flows'] as const,
};

function tagBinom<T extends { provider?: CampaignProvider }>(row: T): T {
  return row.provider ? row : { ...row, provider: 'binom' };
}

export function useBinomCampaigns() {
  return useQuery({
    queryKey: binomKeys.campaigns(),
    queryFn: async () => {
      const response = await api.get<{ data: Campaign[] }>('/binom/campaigns');
      return (response.data?.data ?? []).map(tagBinom);
    },
    staleTime: 60_000,
  });
}

export function useBinomCampaign(campaignId: string) {
  return useQuery({
    queryKey: binomKeys.campaign(campaignId),
    queryFn: async () => {
      const response = await api.get<{ data: CampaignDetail }>(
        `/binom/campaigns/${campaignId}`,
      );
      return tagBinom(response.data.data);
    },
    enabled: !!campaignId,
    staleTime: 60_000,
  });
}

export function useBinomCampaignFlows(campaignId: string) {
  return useQuery({
    queryKey: binomKeys.campaignFlows(campaignId),
    queryFn: async () => {
      const response = await api.get<{ data: Flow[] }>(
        `/binom/campaigns/${campaignId}/flows`,
      );
      return response.data?.data ?? [];
    },
    enabled: !!campaignId,
    staleTime: 60_000,
  });
}
